/**
 * Every visible or screen-reader string of v1 (design-handoff/01 §8), with the dynamic parts as
 * functions. Apostrophes are straight; the ellipsis is the single character "…".
 * Where the prototype hard-codes "October" or "2026", callers pass the real month name and year.
 */

/** `possessive`: always name + "'s" ("Jules's"). The "You" branch only fires for a member literally named "You". */
export const possessive = (name: string): string => (name === 'You' ? 'Your' : `${name}'s`);

/** `mmss`: "0:07". Rounds to whole seconds and floors at 0. */
export function mmss(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${`0${s % 60}`.slice(-2)}`;
}
export const timeOf = (position: number, length: number): string => `${mmss(position)} / ${mmss(length)}`;

/** `kindWord`: what a day square says a post holds. Text is ignored when media is present. */
export function kindWord(items: readonly { readonly t: string }[]): string {
  const photos = items.filter((item) => item.t === 'photo').length;
  if (items.some((item) => item.t === 'video')) return 'video';
  if (photos === 1) return 'photo';
  if (photos > 1) return `${photos} photos`;
  return 'text';
}

const cap = (word: string): string => word.charAt(0).toUpperCase() + word.slice(1);

export const copy = {
  weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  tryAgain: 'Try again',

  // A2 title row
  home: {
    here: (month: string) => `Home, ${month} calendar (you are here)`,
    back: (month: string) => `Home: back to the ${month} calendar`,
  },
  previousMonth: 'Previous month',
  nextMonth: 'Next month',
  previousDay: (dateLong?: string) => (dateLong ? `Previous day, ${dateLong}` : 'Previous day'),
  nextDay: (dateLong?: string) => (dateLong ? `Next day, ${dateLong}` : 'Next day'),
  you: 'You',

  // A3 calendar
  cell: {
    open: (dateLong: string, year: number, today: boolean, posters: number) =>
      `${dateLong} ${year}${today ? ', today' : ''}, ${posters === 0 ? 'nothing shared' : `${posters}${posters === 1 ? ' person' : ' people'} shared`}`,
    future: (dateLong: string, year: number) => `${dateLong} ${year}, not open yet`,
  },
  month: {
    loading: (month: string) => `Loading ${month}…`,
    failed: (month: string) => `Couldn't load ${month}.`,
  },

  // A4 day
  day: {
    square: (name: string, kind: string, isYou: boolean) =>
      `${isYou ? 'You' : name}, ${kind}. Opens ${isYou ? 'your' : possessive(name)} post`,
    loading: (dateLong: string) => `Loading ${dateLong}…`,
    failed: (dateLong: string) => `Couldn't load ${dateLong}.`,
    emptyToday: 'Nobody has shared anything yet today.',
    empty: (dateLong: string) => `Nobody shared anything on ${dateLong}.`,
  },

  // A6 widgets, viewing
  widget: {
    photo: (name?: string) => (name ? `Photo: ${name}` : 'Photo'),
    video: (name?: string) => (name ? `Video: ${name}` : 'Video'),
    playSong: (title: string) => `Play ${title}`,
    pauseSong: (title: string) => `Pause ${title}`,
    songOff: 'Not available right now',
    playVideo: 'Play video',
    pauseVideo: 'Pause video',
    /** Owner, 4 October 2026: videos play muted on their own; these are the sound button and the timeline. */
    unmuteVideo: 'Unmute video',
    muteVideo: 'Mute video',
    videoPosition: 'Position in the video',
    brokenPhoto: "This photo couldn't load.",
    brokenVideo: "This video couldn't load.",
  },

  // A7 widgets, editing
  edit: {
    /** The widget focus button. The song's stray ". " of the prototype is fixed here. */
    focus: (label: string, w: number, h: number, column: number, row: number, how: string) =>
      `${label}, ${w} by ${h}, column ${column}, row ${row}. ${how}. Arrow keys move it, Shift and arrows resize it, Delete removes it`,
    labelText: (text: string) => `Text: ${text ? text.slice(0, 60) : 'empty'}`,
    labelSong: (title: string) => `Song: ${title}`,
    howText: 'Tap or Enter to write. Drag the grip at the bottom to move it',
    howMedia: 'Drag inside to reframe, pinch or swipe to zoom, Alt and arrows reframe, plus and minus zoom. Drag the grip at the bottom to move it',
    howSong: 'Drag the grip at the bottom to move it',
    tapToWrite: 'Tap to write',
    textField: 'Text',
    textPlaceholder: 'Write something',
    toolbar: 'Tools for the selected item',
    smaller: 'Smaller text',
    larger: 'Larger text',
    zoomOut: 'Zoom out',
    zoomIn: 'Zoom in',
    background: 'Background',
    changeSong: 'Change song',
    remove: 'Remove',
    removeThis: (kind: 'photo' | 'video' | 'text' | 'song') => `Remove this ${kind}`,
    solid: 'Solid',
    gradient: 'Gradient',
    swatch: {
      none: 'No background', tile: 'Dark tile', light: 'Light', frost: 'Frosted blur', own: 'Your colour',
      colour: (name: string) => cap(name),
      gOwn: 'Your colour, fading to dusk', gDusk: 'Dusk gradient', gEmber: 'Orange to coral gradient',
      gTide: 'Blue to teal gradient', gBloom: 'Pink to lavender gradient', gMeadow: 'Green to yellow gradient',
      selected: (label: string) => `${label}, selected`,
    },
    emptyLine1: 'Drop photos or videos anywhere on the grid',
    emptyLine2: 'or use Add below for photos, videos and text',
  },

  // A8 compose, A9 profile
  status: {
    saving: 'Saving…',
    draftKept: "Couldn't save. Draft kept.",
    changesKept: "Couldn't save. Your changes are kept.",
  },
  profile: {
    pictureButton: 'Change your picture and colour',
    nameLabel: 'Your name',
    namePlaceholder: 'Your name',
    descLabel: 'Description',
    descPlaceholder: 'A few words about you',
    /** Owner decision of 2 October 2026; not from the prototype. */
    signOut: 'Sign out',
    /** Implementation default (06 §5.5): a profile that is still loading or failed to load; the day's wording. */
    loading: (name: string, isYou: boolean) => `Loading ${isYou ? 'your' : possessive(name)} profile…`,
    failed: (name: string, isYou: boolean) => `Couldn't load ${isYou ? 'your' : possessive(name)} profile.`,
  },

  // A10 island
  island: {
    yourProfile: (name: string) => `Your profile, ${name}`,
    editProfile: 'Edit profile',
    postToday: 'Post today',
    edit: 'Edit',
    /** Owner, 4 October 2026: once you posted, the island leads to your post; Edit is offered on that post. */
    viewPost: 'View your post',
    /** Implementation default (06 §4): the Post button's name when you posted and posts cannot be edited. */
    shared: 'Your post for today is shared',
    discardDraft: 'Discard this draft?',
    deletePost: (dateShort: string) => `Delete your post for ${dateShort}?`,
    discardChanges: 'Discard your changes?',
    keepEditing: 'Keep editing',
    discard: 'Discard',
    delete: 'Delete',
    cancel: 'Cancel',
    colourLines: 'Colour lines',
    add: 'Add to the grid',
    closeAdd: 'Close the add menu',
    look: 'Look',
    share: 'Share',
    save: 'Save',
    done: 'Done',
    saving: 'Saving…',
  },

  // A11 hub
  hub: {
    openProfile: (name: string, isYou: boolean) => `Open ${isYou ? 'your' : possessive(name)} profile`,
    profileOf: (name: string) => `${name}'s profile`,
    previous: (name: string, isYou: boolean) => `Previous: ${isYou ? 'you' : name}`,
    next: (name: string, isYou: boolean) => `Next: ${isYou ? 'you' : name}`,
    previousPerson: 'Previous person',
    nextPerson: 'Next person',
    upCalendar: (month: string) => `Up to the ${month} calendar`,
    upDay: (dateLong: string) => `Up to ${dateLong}, everyone who shared`,
    yourPost: 'Your post',
    postOf: (name: string) => `${possessive(name)} post`,
    backPost: (name: string, isYou: boolean, dateLong: string) => `Back to ${isYou ? 'your' : possessive(name)} post, ${dateLong}`,
    backDay: (dateLong: string) => `Back to ${dateLong}`,
    backCalendar: (month: string) => `Back to the ${month} calendar`,
    addMenu: 'Add to the grid',
    media: 'Photo or video', mediaAria: 'Add a photo or video',
    text: 'Text', textAria: 'Add text',
    /** Owner, 4 October 2026: take a picture or a video with the phone's camera. One entry each: asked for both at once, Android opens its gallery. */
    camera: 'Camera', cameraAria: 'Take a photo',
    record: 'Record video', recordAria: 'Record a video',
    song: 'Song', songAria: 'Add a song', songTaken: 'Add a song. You already have one',
    lookTray: 'Your picture and colour',
    picture: (name: string) => `Picture: ${name}`,
    upload: 'Upload a photo for your picture',
    colour: (index: number, selected: boolean) => `Colour ${index} of 8${selected ? ', selected' : ''}`,
  },

  /**
   * The sign-in / sign-up screen. None of this is from the prototype: the owner asked for the
   * screen on 2 October 2026 and reviews its wording.
   */
  auth: {
    signInTitle: 'Sign in',
    signUpTitle: 'Create your account',
    forgotTitle: 'Reset your password',
    resetTitle: 'Choose a new password',
    verifyTitle: 'Check your email',
    /** Implementation default (P8): a verified account without a profile chooses its name and handle. */
    finishTitle: 'Finish your account',
    finishBody: 'Choose the name and handle people will see.',
    finish: 'Continue', finishing: 'Saving…',
    /** Implementation default (owner decision of 3 October 2026: ask for the colour at the first sign-in). */
    colourTitle: 'Choose your colour',
    colourBody: 'It marks your posts on the calendar. You can change it later in Look.',
    signOut: 'Sign out',
    name: 'Name', namePlaceholder: 'Your name',
    handle: 'Handle', handlePlaceholder: 'your_handle',
    email: 'Email', emailPlaceholder: 'you@example.com',
    password: 'Password', newPassword: 'New password',
    passwordHelp: 'At least 8 characters',
    signIn: 'Sign in', signingIn: 'Signing in…',
    createAccount: 'Create account', creating: 'Creating…',
    /** Owner, 4 October 2026: the question under the title, next to the button that leads to the other step. */
    haveAccountAsk: 'Already have an account?', newHereAsk: 'New here?',
    /** Between the form's own button and Google's. */
    or: 'or',
    forgot: 'Forgot your password?',
    sendLink: 'Send reset link', sending: 'Sending…',
    savePassword: 'Save password', saving: 'Saving…',
    back: 'Back to sign in',
    google: 'Continue with Google',
    resend: 'Send it again',
    verifyBody: (email: string) => `We sent a link to ${email}. Open it to confirm your address, then sign in.`,
    resent: 'Sent again.',
    resetSent: 'If that account exists, a reset email is on its way.',
    resetDone: 'Password updated. Sign in with your new password.',
    error: {
      invalid: 'That email and password do not match.',
      unverified: 'Confirm your email address first. We can send the link again.',
      handle_taken: 'That handle is already taken.',
      handle_invalid: 'Use 3 to 30 characters: a letter first, then letters, digits or _.',
      email_taken: 'That email already has an account. Sign in instead.',
      email_invalid: 'That does not look like an email address.',
      password_weak: 'Use at least 8 characters.',
      link_expired: 'That reset link no longer works. Ask for a new one.',
      too_many: 'Too many tries. Wait a moment, then try again.',
      network: "Couldn't reach the server. Check your connection and try again.",
      server: 'Something went wrong. Try again.',
      nameMissing: 'Enter your name.',
    },
  },

  // A12 toasts
  toast: {
    /** Owner, 4 October 2026: a page address (kalndar.com/<handle>, /<date>/<handle>) that leads nowhere. */
    noSuchHandle: (handle: string) => `Nobody here has the handle @${handle}.`,
    noPostThatDay: (handle: string, dateLong: string) => `@${handle} didn't share anything on ${dateLong}.`,
    pageFailed: "Couldn't open that page. Try again.",
    shared: (dateLong: string) => `Shared to ${dateLong}`,
    saved: 'Changes saved',
    deleted: 'Your post was deleted',
    profileSaved: 'Profile saved',
    wrongType: (fileName: string) => `${fileName || 'That file'} couldn't be added. Only photos and videos work.`,
    noSpaceFiles: (count: number) => `No free space for ${count === 1 ? 'one file' : `${count} files`}. Remove or shrink something first.`,
    noSpace: 'No free space on the grid. Remove or shrink something first.',
    /** Implementation default (04 §11.7): a photo or video failed to upload. */
    uploadFailed: (fileName: string) => `${fileName || 'That file'} couldn't be added.`,
    /** Implementation default: signing out failed (not designed). */
    signOutFailed: "Couldn't sign out. Try again.",
    /** Owner, 4 October 2026: GIFs work in posts and on profile grids, not as your picture. */
    gifPicture: (fileName: string) => `${fileName || 'That GIF'} can't be your picture. GIFs work in posts and on your profile.`,
    /** Used when capabilities.video is false (06 §4). */
    photosOnly: (fileName: string) => `${fileName || 'That file'} couldn't be added. Only photos work.`,
  },
} as const;
